import type { ReactNode } from "react";
import type { FieldArrayPath, FieldArrayWithId, FieldValues } from "react-hook-form";

type RepeatablePeopleSectionProps<
  TFieldValues extends FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues>,
> = {
  title: string;
  helperText?: ReactNode;
  fields: FieldArrayWithId<TFieldValues, TFieldArrayName, "id">[];
  canAddItem: boolean;
  onAddItem: () => void;
  onRemoveItem: (index: number) => void;
  renderItem: (params: {
    field: FieldArrayWithId<TFieldValues, TFieldArrayName, "id">;
    index: number;
    isPrimary: boolean;
    canAddItem: boolean;
    canRemoveItem: boolean;
    onAddItem: () => void;
    onRemoveItem: () => void;
  }) => ReactNode;
  additionalTravellersLayout?: "stacked" | "grid";
  additionalTravellersGridClassName?: string;
};

export function RepeatablePeopleSection<
  TFieldValues extends FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues>,
>({
  title,
  helperText,
  fields,
  canAddItem,
  onAddItem,
  onRemoveItem,
  renderItem,
  additionalTravellersLayout = "stacked",
  additionalTravellersGridClassName,
}: RepeatablePeopleSectionProps<TFieldValues, TFieldArrayName>) {
  const gridLayoutClassName =
    additionalTravellersGridClassName ?? "grid gap-4 md:grid-cols-2 lg:grid-cols-3";

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <h3 className="text-[12px] font-semibold text-theme-text">{title}</h3>
        {helperText ? <p className="text-xs text-theme-text-soft">{helperText}</p> : null}
      </div>

      <div className={additionalTravellersLayout === "grid" ? gridLayoutClassName : "space-y-5"}>
        {fields.map((field, index) =>
          renderItem({
            field,
            index,
            isPrimary: index === 0,
            canAddItem,
            canRemoveItem: fields.length > 1,
            onAddItem,
            onRemoveItem: () => onRemoveItem(index),
          })
        )}
      </div>
    </div>
  );
}
